import React from 'react';
import { Switch, Tooltip } from '@knockrentals/knock-shared-web';
import TitleWithTooltip from './Components/TitleWithTooltip';
import { getOfferTimes } from '../VirtualAgentLookupTables';
import { SettingSelect } from './Components/SettingSelect';
import {
  CALL_ROUTING,
  RESIDENT_CALL_ROUTING,
  GENERAL_SETTINGS_COLUMN_NAMES,
  GENERAL_SETTINGS_FIELDS,
  GENERAL_SETTINGS_TOOL_TIPS,
} from './constants';

const isAIProductsInactive = (rowData) =>
  !rowData.leasingEnabled && !rowData.residentEnabled;

const isResidentAIInactive = (rowData) => !rowData.residentEnabled;

const isMissedCallRoutingActive = (rowData) =>
  rowData[GENERAL_SETTINGS_FIELDS.CALL_ROUTING] === 'MISSED_CALLS' ||
  rowData[GENERAL_SETTINGS_FIELDS.RESIDENT_CALL_ROUTING] === 'MISSED_CALLS';

const isRingTimeDisabled = (rowData) =>
  isAIProductsInactive(rowData) || !isMissedCallRoutingActive(rowData);

const getRingTimeToolTip = (rowData) => {
  if (isAIProductsInactive(rowData)) {
    return 'AI Products are not active';
  } else if (!isMissedCallRoutingActive(rowData)) {
    return "Neither Call Routing nor Resident Call Routing is set to 'Missed'.";
  } else {
    return '';
  }
};

const isAnswerVerificationDisabled = (rowData) =>
  isAIProductsInactive(rowData) || !isMissedCallRoutingActive(rowData);

const getAnswerVerificationToolTip = (rowData) => {
  if (isAIProductsInactive(rowData)) {
    return 'AI Products are not active';
  } else if (!isMissedCallRoutingActive(rowData)) {
    return "Neither Call Routing nor Resident Call Routing is set to 'Missed'.";
  } else {
    return '';
  }
};

export const getGeneralSettingsColumns = (saveVaData) => {
  const tableArray = [
    {
      title: 'Properties',
      width: '120px',
      field: 'propertyName',
      cellStyle: {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        backgroundColor: '#fff',
      },
      headerStyle: {
        left: '0px',
        zIndex: 4,
      },
    },
    {
      title: TitleWithTooltip(
        GENERAL_SETTINGS_COLUMN_NAMES[GENERAL_SETTINGS_FIELDS.CALL_ROUTING],
      ),
      tooltip: GENERAL_SETTINGS_TOOL_TIPS[GENERAL_SETTINGS_FIELDS.CALL_ROUTING],
      field: GENERAL_SETTINGS_FIELDS.CALL_ROUTING,
      width: '150px',
      cellStyle: {
        padding: '8px 16px',
        overflow: 'visible',
      },
      render: (rowData) => {
        return (
          <Tooltip
            title={
              rowData.leasingEnabled || rowData.residentEnabled
                ? ''
                : 'AI Products not active'
            }
          >
            <span>
              <SettingSelect
                handleOnSelect={(value) => {
                  saveVaData(
                    rowData,
                    GENERAL_SETTINGS_FIELDS.CALL_ROUTING,
                    value,
                  );
                }}
                id={'call-routing'}
                isDisabled={isAIProductsInactive(rowData)}
                options={CALL_ROUTING}
                value={rowData[GENERAL_SETTINGS_FIELDS.CALL_ROUTING]}
              />
            </span>
          </Tooltip>
        );
      },
    },

    {
      title: TitleWithTooltip(
        GENERAL_SETTINGS_COLUMN_NAMES[
          GENERAL_SETTINGS_FIELDS.RESIDENT_CALL_ROUTING
        ],
      ),
      tooltip:
        GENERAL_SETTINGS_TOOL_TIPS[GENERAL_SETTINGS_FIELDS.RESIDENT_CALL_ROUTING],
      field: GENERAL_SETTINGS_FIELDS.RESIDENT_CALL_ROUTING,
      width: '150px',
      cellStyle: {
        padding: '8px 16px',
        overflow: 'visible',
      },
      render: (rowData) => {
        return (
          <Tooltip
            title={
              isResidentAIInactive(rowData)
                ? 'AI Resident Product not active'
                : ''
            }
          >
            <span>
              <SettingSelect
                handleOnSelect={(value) => {
                  saveVaData(
                    rowData,
                    GENERAL_SETTINGS_FIELDS.RESIDENT_CALL_ROUTING,
                    value,
                  );
                }}
                id={'resident-call-routing'}
                isDisabled={isResidentAIInactive(rowData)}
                options={RESIDENT_CALL_ROUTING}
                value={
                  rowData[GENERAL_SETTINGS_FIELDS.RESIDENT_CALL_ROUTING] ??
                  'OFF'
                }
              />
            </span>
          </Tooltip>
        );
      },
    },

    {
      title: TitleWithTooltip(
        GENERAL_SETTINGS_COLUMN_NAMES[GENERAL_SETTINGS_FIELDS.OFFER_TIMES],
      ),
      tooltip: GENERAL_SETTINGS_TOOL_TIPS[GENERAL_SETTINGS_FIELDS.OFFER_TIMES],
      field: GENERAL_SETTINGS_FIELDS.OFFER_TIMES,
      width: '150px',
      cellStyle: {
        padding: '8px 16px',
        overflow: 'visible',
      },
      render: (rowData) => {
        return (
          <Tooltip title={getRingTimeToolTip(rowData)}>
            <span>
              <SettingSelect
                handleOnSelect={(value) => {
                  saveVaData(
                    rowData,
                    GENERAL_SETTINGS_FIELDS.OFFER_TIMES,
                    value,
                  );
                }}
                id={'ring-time-dropdown'}
                isDisabled={isRingTimeDisabled(rowData)}
                options={getOfferTimes()}
                value={String(
                  rowData[GENERAL_SETTINGS_FIELDS.OFFER_TIMES] ?? '15',
                )}
              />
            </span>
          </Tooltip>
        );
      },
    },

    {
      title: TitleWithTooltip(
        GENERAL_SETTINGS_COLUMN_NAMES[
          GENERAL_SETTINGS_FIELDS.ANSWER_VERIFICATION
        ],
      ),
      tooltip:
        GENERAL_SETTINGS_TOOL_TIPS[GENERAL_SETTINGS_FIELDS.ANSWER_VERIFICATION],
      field: GENERAL_SETTINGS_FIELDS.ANSWER_VERIFICATION,
      width: '100px',
      render: (rowData) => {
        return (
          <Tooltip title={getAnswerVerificationToolTip(rowData)}>
            <span>
              <Switch
                checked={!!rowData.is_answer_verification}
                color="primary"
                disabled={isAnswerVerificationDisabled(rowData)}
                onChange={(event) =>
                  saveVaData(
                    rowData,
                    GENERAL_SETTINGS_FIELDS.ANSWER_VERIFICATION,
                    event.target.checked,
                  )
                }
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: TitleWithTooltip(
        GENERAL_SETTINGS_COLUMN_NAMES[
          GENERAL_SETTINGS_FIELDS.RPCC_AGENT_ROLLOVER
        ],
      ),
      tooltip:
        GENERAL_SETTINGS_TOOL_TIPS[GENERAL_SETTINGS_FIELDS.RPCC_AGENT_ROLLOVER],
      field: GENERAL_SETTINGS_FIELDS.RPCC_AGENT_ROLLOVER,
      width: '100px',
      render: (rowData) => {
        return (
          <Tooltip
            title={
              rowData.leasingEnabled ? '' : 'AI Leasing Product not active'
            }
          >
            <span>
              <Switch
                inputProps={{
                  'aria-label': GENERAL_SETTINGS_FIELDS.RPCC_AGENT_ROLLOVER,
                }}
                checked={!!rowData.rpcc_agent_rollover}
                color="primary"
                disabled={!rowData.leasingEnabled}
                name="rpcc-agent-rollover"
                onChange={(event) =>
                  saveVaData(
                    rowData,
                    GENERAL_SETTINGS_FIELDS.RPCC_AGENT_ROLLOVER,
                    event.target.checked,
                  )
                }
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: TitleWithTooltip(
        GENERAL_SETTINGS_COLUMN_NAMES[GENERAL_SETTINGS_FIELDS.CHAT_ROLLOVER],
      ),
      tooltip:
        GENERAL_SETTINGS_TOOL_TIPS[GENERAL_SETTINGS_FIELDS.CHAT_ROLLOVER],
      field: GENERAL_SETTINGS_FIELDS.CHAT_ROLLOVER,
      width: '100px',
      render: (rowData) => {
        return (
          <Tooltip
            title={
              rowData.leasingEnabled ? '' : 'AI Leasing Product not active'
            }
          >
            <span>
              <Switch
                inputProps={{
                  'aria-label': GENERAL_SETTINGS_FIELDS.CHAT_ROLLOVER,
                }}
                checked={rowData.chat_rollover === 'RPCC'}
                color="primary"
                disabled={!rowData.leasingEnabled}
                name="rpcc-chat-rollover"
                onChange={(event) =>
                  saveVaData(
                    rowData,
                    GENERAL_SETTINGS_FIELDS.CHAT_ROLLOVER,
                    event.target.checked,
                  )
                }
              />
            </span>
          </Tooltip>
        );
      },
    },
  ];
  return tableArray;
};
